import type { TeleStaffChatMessage } from '../../types/api';

function readStaffName(m: Record<string, unknown>): string | undefined {
  for (const key of ['staffName', 'senderName', 'displayName', 'employeeName', 'empName']) {
    const raw = m[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

function readStaffEmpId(m: Record<string, unknown>): number | undefined {
  for (const key of ['staffEmpId', 'empId', 'employeeId']) {
    const raw = m[key];
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  }
  return undefined;
}

/**
 * Fields stamped onto every staff send so TeleChatConversation JSON stays clean on write.
 * Required write shape for staff lines: { sender:"staff", text, ts, staffName, staffEmpId }.
 * Use this helper everywhere staff posts (TeleVisit + Normal Support).
 */
export function staffChatSenderFields(
  staff: { id?: number | null; name?: string | null; displayName?: string | null } | null | undefined,
): Pick<TeleStaffChatMessage, 'staffName' | 'staffEmpId'> {
  const name = (staff?.displayName || staff?.name || '').trim();
  const id = staff?.id;
  return {
    ...(name ? { staffName: name } : {}),
    ...(id != null && id > 0 ? { staffEmpId: id } : {}),
  };
}

/** Client message id for idempotent sends (server de-duplicates retries with the same id). */
export function newClientMsgId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export type TeleChatLifecycleStatus = 'open' | 'solved' | 'pendingPartner' | 'closed';

/** Lifecycle status from API / SignalR fields (older payloads only carry the booleans). */
export function resolveTeleChatStatus(
  f: { status?: string | null; isClosed?: boolean | null; isClosingPending?: boolean | null; isPendingPartner?: boolean | null } | null | undefined,
): TeleChatLifecycleStatus | undefined {
  if (!f) return undefined;
  const s = (f.status ?? '').trim();
  if (s === 'open' || s === 'solved' || s === 'pendingPartner' || s === 'closed') return s;
  if (f.isClosed) return 'closed';
  if (f.isClosingPending) return 'solved';
  if (f.isPendingPartner) return 'pendingPartner';
  if (f.isClosed === false || f.isClosingPending === false) return 'open';
  return undefined;
}

export const TELE_CHAT_STATUS_LABEL: Record<TeleChatLifecycleStatus, string> = {
  open: 'Open',
  solved: 'Solved',
  pendingPartner: 'Waiting for reply',
  closed: 'Closed',
};

/** Serialize one chat line for POST upsert — never drops staffName/staffEmpId on staff lines. */
export function serializeStaffChatMessage(m: TeleStaffChatMessage): Record<string, unknown> {
  const row: Record<string, unknown> = { sender: m.sender, text: m.text, ts: m.ts };
  if (m.id) row.id = m.id;
  if (m.sender === 'staff') {
    const name = (m.staffName || '').trim();
    if (name) row.staffName = name;
    if (m.staffEmpId != null && m.staffEmpId > 0) row.staffEmpId = m.staffEmpId;
  }
  if (m.imageUlid) {
    row.imageUlid = m.imageUlid;
    row.imageFileName = m.imageFileName ?? 'image';
    row.imageMimeType = m.imageMimeType ?? 'image/jpeg';
  }
  return row;
}

function parseImageUlid(m: Record<string, unknown>): string | undefined {
  const raw = m.imageUlid;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  return raw.trim();
}

export function parseChatMessages(raw: string | undefined | null): TeleStaffChatMessage[] {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: TeleStaffChatMessage[] = [];
    for (const item of parsed) {
      if (item == null || typeof item !== 'object') continue;
      const m = item as Record<string, unknown>;
      const sender = m.sender;
      const text = typeof m.text === 'string' ? m.text : '';
      const tsRaw = m.ts;
      let ts: number;
      if (typeof tsRaw === 'number' && Number.isFinite(tsRaw)) {
        ts = tsRaw;
      } else if (typeof tsRaw === 'string' && Number.isFinite(Number(tsRaw))) {
        ts = Number(tsRaw);
      } else {
        continue;
      }
      const imageUlid = parseImageUlid(m);
      if (
        (sender !== 'patient' && sender !== 'staff' && sender !== 'system' && sender !== 'partner')
        || (!text.trim() && !imageUlid)
      ) {
        continue;
      }
      const row: TeleStaffChatMessage = {
        sender: sender as TeleStaffChatMessage['sender'],
        text,
        ts,
      };
      if (typeof m.id === 'string' && m.id.trim()) row.id = m.id.trim();
      if (imageUlid) {
        row.imageUlid = imageUlid;
        if (typeof m.imageFileName === 'string') row.imageFileName = m.imageFileName;
        if (typeof m.imageMimeType === 'string') row.imageMimeType = m.imageMimeType;
      }
      if (sender === 'staff') {
        const staffName = readStaffName(m);
        const staffEmpId = readStaffEmpId(m);
        if (staffName) row.staffName = staffName;
        if (staffEmpId) row.staffEmpId = staffEmpId;
      }
      out.push(row);
    }
    return out;
  } catch {
    return [];
  }
}

export function sameMessageList(a: TeleStaffChatMessage[], b: TeleStaffChatMessage[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Auto-posted payment confirmation — not a real patient message for unread badges. */
export function isAutomatedPaymentChatMessage(text: string | undefined | null): boolean {
  if (!text?.trim()) return false;
  const trimmed = text.trimStart();
  return (
    trimmed.startsWith('✅ Payment successful') ||
    trimmed.startsWith('Payment successful')
  );
}

export function countsAsStaffUnreadPatientMessage(m: TeleStaffChatMessage): boolean {
  return (
    (m.sender === 'patient' || m.sender === 'partner') &&
    !isAutomatedPaymentChatMessage(m.text)
  );
}

/** True when two rows represent the same chat line (avoids duplicate append on merge-save). */
export function chatMessagesEquivalent(a: TeleStaffChatMessage, b: TeleStaffChatMessage): boolean {
  if (a.sender !== b.sender) return false;
  if ((a.imageUlid ?? '') !== (b.imageUlid ?? '')) return false;
  if (a.text !== b.text) return false;
  return Math.abs(a.ts - b.ts) < 5000;
}

/** Append a message to server state without dropping out-of-band rows (e.g. patient chat images). */
export function appendChatMessage(
  server: TeleStaffChatMessage[],
  msg: TeleStaffChatMessage,
): TeleStaffChatMessage[] {
  if (server.some((m) => chatMessagesEquivalent(m, msg))) return server;
  return [...server, msg];
}

/** Initials for Messenger-style chat heads (first + last word). */
export function chatHeadInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const word = parts[0];
    return word.slice(0, Math.min(2, word.length)).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
